import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/stateHooks';
import { fetchETLConfigurations } from '../redux/action/etl.action';
import { APP_FEATURES, APP_ROUTES } from '../constants';
import { SESSION_STORAGE_KEY } from '../constant/auth.constant';
import { getSessionItemWithExpiry } from '../utility/utility';

interface ETLGuardProps {
  children: React.ReactNode;
}

/**
 * ETLGuard — Route-level guard for Super Admin ETL setup enforcement
 *
 * Reads setupComplete + loading from Redux state.etl.
 * If the user is a Super Admin (has INITIAL_SYSTEM_SETUP privilege)
 * and setupComplete is false, redirects to ETL Dashboard.
 *
 * Does NOT redirect while loading (prevents premature redirect before API resolves).
 * Non-Super Admin users pass through unaffected.
 */
const ETLGuard: React.FC<ETLGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { setupComplete, loading } = useAppSelector((state) => state.etl);

  // Check if user is Super Admin (has INITIAL_SYSTEM_SETUP privilege)
  const storedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);
  const privileges: string[] = storedPrivileges ? JSON.parse(storedPrivileges) : [];
  const isSuperAdmin = privileges.includes(APP_FEATURES.INITIAL_SYSTEM_SETUP);

  // Fetch ETL configurations on mount if Super Admin (to get setupComplete status)
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchETLConfigurations());
    }
  }, [dispatch, isSuperAdmin]);

  // Redirect logic
  useEffect(() => {
    // Don't redirect while loading (wait for API to resolve)
    if (loading) return;

    // Only apply gate to Super Admins
    if (!isSuperAdmin) return;

    // Check if user is on any ETL-related page
    const isOnETLPages =
      location.pathname === APP_ROUTES.ETL_DASHBOARD ||
      location.pathname === APP_ROUTES.ETL_QUICK_SETUP ||
      location.pathname.startsWith(APP_ROUTES.ETL_QUICK_SETUP);

    // If setup is not complete and user is not on ETL pages, redirect to dashboard
    if (!setupComplete && !isOnETLPages) {
      navigate(APP_ROUTES.ETL_DASHBOARD, { replace: true });
    }
  }, [loading, isSuperAdmin, setupComplete, location.pathname, navigate]);

  return <>{children}</>;
};

export default ETLGuard;
